import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { StationExclusion } from "../../../types/alerting";
import { MAX_NOTE_LENGTH } from "./exclusions";

interface EditExclusionNoteDialogProps {
  exclusion: StationExclusion | null;
  saving: boolean;
  /** Server-side rejection to show inside the dialog, so the form stays open. */
  errorMessage: string | null;
  onSave: (note: string | null) => void;
  onClose: () => void;
}

export default function EditExclusionNoteDialog({
  exclusion,
  saving,
  errorMessage,
  onSave,
  onClose,
}: EditExclusionNoteDialogProps) {
  const [note, setNote] = useState("");

  useEffect(() => {
    setNote(exclusion?.note ?? "");
  }, [exclusion]);

  const isTooLong = note.length > MAX_NOTE_LENGTH;

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" text onClick={onClose} disabled={saving} />
      <Button
        label="Save"
        icon="pi pi-check"
        loading={saving}
        disabled={isTooLong || saving}
        onClick={() => onSave(note.trim() || null)}
      />
    </div>
  );

  return (
    <Dialog
      header="Edit note"
      visible={!!exclusion}
      onHide={onClose}
      footer={footer}
      style={{ width: "32rem", maxWidth: "95vw" }}
      draggable={false}
    >
      {exclusion && (
        <div className="flex flex-column gap-3">
          <p className="m-0 text-color-secondary">
            Why {exclusion.stationName ?? exclusion.stationId} is excluded from owner alerting. Leave
            it empty to clear the note.
          </p>

          <div className="flex flex-column gap-2">
            <label htmlFor="exclusion-note-edit" className="font-semibold">
              Note
            </label>
            <InputTextarea
              id="exclusion-note-edit"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              autoResize
              disabled={saving}
              className={isTooLong ? "p-invalid" : ""}
              placeholder="e.g. electrician booked for next week"
            />
            <small className={isTooLong ? "p-error" : "text-color-secondary"}>
              {`${note.length}/${MAX_NOTE_LENGTH}`}
            </small>
          </div>

          {errorMessage && <Message severity="error" text={errorMessage} />}
        </div>
      )}
    </Dialog>
  );
}
